import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Location } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';

import { GlobalsService } from '../../services/globals.service';
import { InventoryService } from '../../services/inventory.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { NgxDropzoneChangeEvent } from 'ngx-dropzone';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'app-inventory-upload',
	templateUrl: './inventory-upload.component.html',
	styleUrls: ['./inventory-upload.component.scss'],
})
export class InventoryUploadComponent implements OnInit, OnDestroy {
	@ViewChild('eventRef') eventRef: ElementRef;

	files: File[] = [];
	UploadedHeaders: string[] = [];
	uploaddata: any = false;
	mapcols: any = [];
	mapped: any = [];
	optionForm = new UntypedFormGroup({});
	mappingForm = new UntypedFormGroup({});
	rowNumber = new UntypedFormControl('1');
	color: any = 'blue';
	config: any = {};
	sending = false;
	filelist: any = [];
	mappeddata: any = [];
	preview: any = false;
	investigation: any = '';
	investigated: any = '';

	uploadHelp = false;
	processing_recs = false;
	inventoryReviewReady = false;

	/** Max rows shown in the mapping preview grid (all rows still used on Pre-Process). */
	readonly inventoryMappingPreviewMax = 50;

	private readonly destroy$ = new Subject<void>();

	private readonly inventoryUploadAllowedExtensions = ['.csv', '.xlsx', '.xls'];

	/** Header text (normalized) → best-guess scores for auto-mapping. */
	private readonly fieldHeaderSynonyms: Record<string, string[]> = {
		stockid: ['stockid', 'sku', 'itemnumber', 'itemnum', 'item', 'partnumber', 'partnum', 'part', 'productcode', 'stylenum', 'style', 'product'],
		salestype: ['salestype', 'pricetype', 'pricelevel', 'price level', 'custtype', 'customertype', 'level'],
		currabrev: ['currency', 'curr', 'currabrev', 'currabbr'],
		lineid: ['lineid', 'productline', 'pline', 'line', 'prodline'],
		categoryid: ['category', 'categoryid', 'cat', 'dept', 'department'],
		description: ['description', 'desc', 'longdesc', 'title', 'name'],
		units: ['units', 'uom', 'unitofmeasure', 'unit of measure', 'um'],
		cost: ['cost', 'unitcost', 'standardcost', 'stdcost'],
		taxcatid: ['taxcat', 'taxcategory', 'tax'],
		barcode: ['barcode', 'upc', 'ean', 'gtin'],
		additionalfee: ['excise', 'additionalfee', 'fee'],
		sort: ['sort', 'lookup1', 'lookup 1', 'search1'],
		sort2: ['sort2', 'lookup2', 'lookup 2', 'search2'],
		mfgpart: ['mfgpart', 'mfg part', 'manufacturerpart', 'mpn', 'oem'],
		mfgname: ['mfgname', 'brand', 'mfg', 'make', 'manufacturer'],
		min: ['min', 'minimum', 'minstock', 'min stock'],
		max: ['max', 'maximum', 'maxstock', 'max stock'],
		price: ['price', 'salesprice', 'listprice', 'sellprice', 'unitprice'],
		startdate: ['startdate', 'pricefrom', 'effectivefrom', 'begindate', 'fromdate'],
		enddate: ['enddate', 'priceto', 'effectiveto', 'todate'],
		supplierno: ['supplier', 'supplierno', 'vendor', 'vendorno', 'vcode', 'vendcode'],
		supplierprice: ['supplierprice', 'purchprice', 'purchaseprice', 'buyprice', 'netprice'],
		suppliersuom: ['suppliersuom', 'purchuom', 'purchaseuom'],
		supplierdescription: ['supplierdescription', 'vendordesc'],
		conversionfactor: ['conversion', 'conversionfactor', 'cf', 'factor'],
		suppliers_partno: ['supplierspart', 'vendorpart', 'vpn', 'suppart', 'vpart'],
		effectivefrom: ['effectivefrom', 'purchfrom', 'podate'],
		minorderqty: ['minorder', 'minqty', 'moq'],
		maxorderqty: ['maxorder', 'maxqty'],
		quantitybreak: ['qtybreak', 'quantitybreak', 'breakqty', 'qb', 'qty break'],
		breakprice: ['breakprice', 'brkprice', 'qtyprice'],
		breakstartdate: ['breakstart', 'brkstart', 'breakstartdate'],
		breakenddate: ['breakend', 'brkend', 'breakenddate'],
	};

	constructor(
		private globalSearchService: GlobalSearchService,
		private modalService: NgbModal,
		private fb: UntypedFormBuilder,
		private inventoryService: InventoryService,
		private location: Location,
		private globalsService: GlobalsService,
		private cdr: ChangeDetectorRef
	) {
		this.color = this.globalSearchService.getColor();
		this.config = this.globalsService.getApiConfig();
		this.globalSearchService.configsubscription.subscribe((r: any) => {
			this.config = r;
		});
	}

	ngOnInit(): void {
		this.inventoryService.getBatchUpload().subscribe((r) => {
			this.filelist = r;
		});

		this.optionForm = this.fb.group({
			stockmaster: [false],
			prices: [false],
			purchdata: [false],
			pricematrix: [false],
			tireinfo: [false],
		});
		this.optionForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.adjustMapping();
		});
		this.adjustMapping();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	toggleUploadHelp(): void {
		this.uploadHelp = !this.uploadHelp;
	}

	formatFileSizeKb(bytes: number): string {
		return (bytes / 1024).toFixed(1);
	}

	private normalizeForMatch(s: string): string {
		return String(s || '')
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, '');
	}

	private scoreFieldHeader(field: string, header: string): number {
		const hn = this.normalizeForMatch(header);
		if (!hn) {
			return 0;
		}
		const fn = this.normalizeForMatch(field);
		if (hn === fn) {
			return 100;
		}
		let best = 0;
		const syns = this.fieldHeaderSynonyms[field];
		if (syns) {
			for (const syn of syns) {
				const sn = this.normalizeForMatch(syn);
				if (!sn) {
					continue;
				}
				if (hn === sn) {
					best = Math.max(best, 92);
				} else if (hn.includes(sn) && sn.length >= 4) {
					best = Math.max(best, 72);
				} else if (hn.includes(sn) && sn.length >= 3) {
					best = Math.max(best, 52);
				} else if (sn.includes(hn) && hn.length >= 4) {
					best = Math.max(best, 58);
				}
			}
		}
		return best;
	}

	/**
	 * Greedy match: each file column → at most one system field, and each system field → at most one file column.
	 * @param options.replaceAll When true, clear all mappings first (e.g. user clicked Suggest again).
	 * @param options.onlyEmpty When true, only fill controls that are still empty (after import toggles change).
	 */
	applyAutoMapping(options?: { replaceAll?: boolean; onlyEmpty?: boolean }): void {
		if (!this.fileColumnsForMapping.length || !this.mappingForm) {
			return;
		}
		const replaceAll = options?.replaceAll === true;
		const onlyEmpty = options?.onlyEmpty === true;
		if (replaceAll) {
			this.mapcols.forEach((m) => this.mappingForm.get(m.field)?.setValue(''));
		}
		const fieldIndex = (f: string) => this.mapcols.findIndex((x) => x.field === f);
		const pairs: { field: string; header: string; score: number }[] = [];
		for (const m of this.mapcols) {
			for (const h of this.fileColumnsForMapping) {
				const sc = this.scoreFieldHeader(m.field, h);
				if (sc >= 40) {
					pairs.push({ field: m.field, header: h, score: sc });
				}
			}
		}
		pairs.sort((a, b) => {
			if (b.score !== a.score) {
				return b.score - a.score;
			}
			return fieldIndex(a.field) - fieldIndex(b.field);
		});
		const usedHeaders = new Set<string>();
		if (!replaceAll) {
			this.mapcols.forEach((m) => {
				const v = this.mappingForm.get(m.field)?.value;
				if (v) {
					usedHeaders.add(String(v));
				}
			});
		}
		const usedFields = new Set<string>();
		for (const p of pairs) {
			if (usedHeaders.has(p.header) || usedFields.has(p.field)) {
				continue;
			}
			const ctrl = this.mappingForm.get(p.field);
			if (!ctrl) {
				continue;
			}
			if (onlyEmpty && ctrl.value) {
				continue;
			}
			ctrl.setValue(p.header);
			usedHeaders.add(p.header);
			usedFields.add(p.field);
		}
		this.cdr.markForCheck();
	}

	suggestMappings(): void {
		this.applyAutoMapping({ replaceAll: true });
		this.globalSearchService.showNotification('Suggested column mappings applied — review before Pre-Process.', 'info', 'bottom', 'left');
	}

	clearAllFiles(): void {
		this.files = [];
		this.resetInventoryParseState();
	}

	resetInventoryParseState(): void {
		this.processing_recs = false;
		this.inventoryReviewReady = false;
		this.uploaddata = false;
		this.UploadedHeaders = [];
		this.preview = false;
		this.mappeddata = [];
		this.adjustMapping();
	}

	backFromParse(): void {
		this.resetInventoryParseState();
	}

	private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as ArrayBuffer);
			reader.onerror = () => reject(new Error('read_failed'));
			reader.readAsArrayBuffer(file);
		});
	}

	/** Match sheet header keys to mapping: trim, strip BOM — avoids undefined cells and dropped JSON keys. */
	private normalizeSheetHeaderKey(k: string): string {
		return String(k).replace(/^\uFEFF/, '').trim();
	}

	private normalizeSheetRow(row: Record<string, unknown>): Record<string, string> {
		const o: Record<string, string> = {};
		for (const k of Object.keys(row)) {
			if (k === '__rowNum__') {
				continue;
			}
			const nk = this.normalizeSheetHeaderKey(k);
			if (nk === '') {
				continue;
			}
			const v = row[k];
			o[nk] = v === null || v === undefined ? '' : String(v).trim();
		}
		return o;
	}

	/** Value for a mapped file column; never undefined (so JSON payload keeps the key). */
	private cellValueForMappedColumn(row: Record<string, unknown>, fileHeader: string): string {
		const h = this.normalizeSheetHeaderKey(String(fileHeader ?? ''));
		if (!h) {
			return '';
		}
		if (Object.prototype.hasOwnProperty.call(row, h)) {
			const v = row[h];
			return v === null || v === undefined ? '' : String(v).trim();
		}
		for (const k of Object.keys(row)) {
			if (k === '__rowNum__' || k === '_sourceFile') {
				continue;
			}
			if (this.normalizeSheetHeaderKey(k) === h) {
				const v = row[k];
				return v === null || v === undefined ? '' : String(v).trim();
			}
		}
		return '';
	}

	private parseInventorySpreadsheet(file: File): Promise<{ rows: Record<string, string>[]; headers: string[] }> {
		return this.readFileAsArrayBuffer(file).then((buf) => {
			const wb = XLSX.read(buf, { type: 'array', cellDates: true, dense: false });
			const sheetName = wb.SheetNames[0];
			if (!sheetName) {
				return { rows: [], headers: [] };
			}
			const ws = wb.Sheets[sheetName];
			const headerRow0 = Math.max(0, parseInt(String(this.rowNumber.value ?? '1'), 10) - 1);
			const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, {
				range: headerRow0,
				defval: '',
				raw: false,
			});
			const rows: Record<string, string>[] = raw.map((row) => this.normalizeSheetRow(row));
			const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
			return { rows, headers };
		});
	}

	processInventoryFiles(options?: { userInitiated?: boolean }): void {
		const userInitiated = options?.userInitiated === true;
		if (!this.files.length) {
			if (userInitiated) {
				this.globalSearchService.showNotification('Add a file first.', 'info', 'bottom', 'left');
			}
			return;
		}

		const file = this.files[0];

		this.processing_recs = true;
		this.inventoryReviewReady = false;
		this.uploaddata = false;
		this.UploadedHeaders = [];
		this.preview = false;
		this.mappeddata = [];

		const run = () => {
			this.parseInventorySpreadsheet(file)
				.then(({ rows, headers }) => ({ rows, headers, ok: true as const }))
				.catch(() => ({ rows: [] as Record<string, string>[], headers: [] as string[], ok: false as const }))
				.then((r) => {
					this.processing_recs = false;
					if (!r.ok) {
						this.globalSearchService.showNotification(
							`Could not read file: ${file.name}. Use CSV or Excel (.csv, .xlsx, .xls), first sheet.`,
							'danger',
							'bottom',
							'left'
						);
						return;
					}
					const name = file.name;
					const allFileData = r.rows.map((row) => ({ ...row, _sourceFile: name }));
					this.UploadedHeaders = r.headers.length ? [...r.headers] : [];
					if (allFileData.length === 0) {
						this.globalSearchService.showNotification(
							'No data rows found after the header row. Check the header row number.',
							'warning',
							'bottom',
							'left'
						);
						return;
					}
					this.uploaddata = allFileData;
					this.inventoryReviewReady = true;
					this.adjustMapping();
					if (userInitiated) {
						this.globalSearchService.showNotification(
							'Column mappings auto-suggested from headers — confirm, then Pre-Process.',
							'success',
							'bottom',
							'left'
						);
					}
				});
		};

		if (typeof requestAnimationFrame !== 'undefined') {
			requestAnimationFrame(() => run());
		} else {
			setTimeout(() => run(), 0);
		}
	}

	rereadFile(): void {
		this.processInventoryFiles({ userInitiated: true });
	}

	/** Theme key for dropzone styling — matches `card-header-{{color}}-5` options from localStorage. */
	get dropzoneThemeColor(): string {
		const allowed = new Set(['blue', 'azure', 'purple', 'green', 'red']);
		const c = String(this.color || 'blue')
			.toLowerCase()
			.trim();
		return allowed.has(c) ? c : 'blue';
	}

	onSelect(event: NgxDropzoneChangeEvent): void {
		if (!event) {
			return;
		}
		if (event.rejectedFiles?.length) {
			const names = event.rejectedFiles.map((f) => f.name).join(', ');
			this.globalSearchService.showNotification(
				`Not added (type or size): ${names}`,
				'warning',
				'bottom',
				'left'
			);
		}
		if (!event.addedFiles?.length) {
			return;
		}
		const valid: File[] = [];
		const invalidNames: string[] = [];
		for (const f of event.addedFiles) {
			const lower = f.name.toLowerCase();
			if (this.inventoryUploadAllowedExtensions.some((ext) => lower.endsWith(ext))) {
				valid.push(f);
			} else {
				invalidNames.push(f.name);
			}
		}
		if (invalidNames.length) {
			this.globalSearchService.showNotification(
				`Only CSV and Excel are supported. Skipped: ${invalidNames.join(', ')}`,
				'warning',
				'bottom',
				'left'
			);
		}
		if (valid.length) {
			const first = valid[0];
			if (valid.length > 1) {
				this.globalSearchService.showNotification(
					`Only one file at a time — using: ${first.name}`,
					'info',
					'bottom',
					'left'
				);
			}
			if (this.inventoryReviewReady || this.uploaddata) {
				this.resetInventoryParseState();
			}
			this.files = [first];
			const runDrop = () => this.processInventoryFiles();
			if (typeof requestAnimationFrame !== 'undefined') {
				requestAnimationFrame(runDrop);
			} else {
				setTimeout(runDrop, 0);
			}
		}
	}

	onRemove(file: File): void {
		const index = this.files.indexOf(file);
		if (index !== -1) {
			this.files.splice(index, 1);
		}
		if (this.files.length === 0) {
			this.resetInventoryParseState();
		}
	}

	/** Column definitions for current import toggles — single source for mapping UI and CSV template. */
	private buildMapColumnDefinitions(): { field: string; label: string; req: boolean }[] {
		const mapcols: { field: string; label: string; req: boolean }[] = [{ field: 'stockid', label: 'Item Number', req: true }];
		if (this.optionForm.get('prices')?.value === true || this.optionForm.get('pricematrix')?.value === true) {
			mapcols.push({ field: 'salestype', label: 'Price Level', req: true });
			mapcols.push({ field: 'currabrev', label: 'Currency', req: false });
		}
		if (this.optionForm.get('stockmaster')?.value === true) {
			mapcols.push(
				{ field: 'lineid', label: 'Product Line', req: false },
				{ field: 'categoryid', label: 'Item Category', req: false },
				{ field: 'description', label: 'Item Description', req: false },
				{ field: 'units', label: 'Unit of Measure', req: false },
				{ field: 'cost', label: 'Cost', req: false },
				{ field: 'taxcatid', label: 'Tax Category', req: false },
				{ field: 'barcode', label: 'UPC Code', req: false },
				{ field: 'additionalfee', label: 'Excise Tax', req: false },
				{ field: 'sort', label: 'Lookup Field 1', req: false },
				{ field: 'sort2', label: 'Lookup Field 2', req: false },
				{ field: 'mfgpart', label: 'Manufacturer Part Number', req: false },
				{ field: 'mfgname', label: 'Brand', req: false },
				{ field: 'min', label: 'Minimum Stock', req: false },
				{ field: 'max', label: 'Maximum Stock', req: false }
			);
		}
		if (this.optionForm.get('prices')?.value === true) {
			mapcols.push(
				{ field: 'price', label: 'Sales Price', req: true },
				{ field: 'startdate', label: 'Price Start Date', req: false },
				{ field: 'enddate', label: 'Price End Date', req: false }
			);
		}
		if (this.optionForm.get('purchdata')?.value === true) {
			mapcols.push(
				{ field: 'supplierno', label: 'Vendor Code', req: true },
				{ field: 'supplierprice', label: 'Purchase Price', req: true },
				{ field: 'suppliersuom', label: 'Purchase Unit of Measure', req: false },
				{ field: 'supplierdescription', label: 'Supplier Description', req: false },
				{ field: 'conversionfactor', label: 'Conversion Factor', req: false },
				{ field: 'suppliers_partno', label: 'Vendor Part Number', req: false },
				{ field: 'effectivefrom', label: 'Purchase available from date', req: false },
				{ field: 'minorderqty', label: 'Min Purchase Qty', req: false },
				{ field: 'maxorderqty', label: 'Max Purchase Qty', req: false }
			);
		}
		if (this.optionForm.get('pricematrix')?.value === true) {
			mapcols.push(
				{ field: 'quantitybreak', label: 'Price Break Qty', req: true },
				{ field: 'breakprice', label: 'Break Price', req: true },
				{ field: 'breakstartdate', label: 'Price Break Start', req: true },
				{ field: 'breakenddate', label: 'Price Break End', req: true }
			);
		}
		return mapcols;
	}

	adjustMapping(): void {
		const prev: Record<string, string> = {};
		if (this.mappingForm && Object.keys(this.mappingForm.controls).length) {
			for (const key of Object.keys(this.mappingForm.controls)) {
				const v = this.mappingForm.get(key)?.value;
				if (v && this.fileColumnsForMapping.includes(String(v))) {
					prev[key] = String(v);
				}
			}
		}
		this.mapcols = this.buildMapColumnDefinitions();
		this.mappingForm = new UntypedFormGroup({});
		this.mapcols.forEach((m) => {
			const initVal = prev[m.field] ?? '';
			if (m.req === true) {
				this.mappingForm.addControl(m.field, new UntypedFormControl(initVal, [Validators.required]));
			} else {
				this.mappingForm.addControl(m.field, new UntypedFormControl(initVal, []));
			}
		});
		if (this.fileColumnsForMapping.length) {
			this.applyAutoMapping({ onlyEmpty: true });
		}
		this.cdr.markForCheck();
	}

	/** File columns shown in the mapping table (excludes internal keys). */
	get fileColumnsForMapping(): string[] {
		return (this.UploadedHeaders || []).filter((k) => k !== '_sourceFile' && k !== '__rowNum__');
	}

	get mappingPreviewRows(): any[] {
		const rows = Array.isArray(this.uploaddata) ? this.uploaddata : [];
		return rows.slice(0, this.inventoryMappingPreviewMax);
	}

	get mappingPreviewTotal(): number {
		return Array.isArray(this.uploaddata) ? this.uploaddata.length : 0;
	}

	/** Which system field (mapcols.field) uses this file column, if any. */
	getSystemFieldForFileHeader(fileHeader: string): string {
		const m = this.mapcols.find((mc) => this.mappingForm?.get(mc.field)?.value === fileHeader);
		return m ? m.field : '';
	}

	/** True if `fieldKey` is already mapped to a different file column (option disabled for this header). */
	isFieldMappedToOtherHeader(fieldKey: string, currentFileHeader: string): boolean {
		const v = this.mappingForm?.get(fieldKey)?.value;
		return !!v && String(v) !== currentFileHeader;
	}

	onFileColumnMappingChange(fileHeader: string, newField: string): void {
		if (!this.mappingForm) {
			return;
		}
		this.mapcols.forEach((mc) => {
			const c = this.mappingForm.get(mc.field);
			if (c?.value === fileHeader) {
				c.setValue('');
			}
		});
		if (newField) {
			this.mappingForm.get(newField)?.setValue(fileHeader);
		}
		this.cdr.markForCheck();
	}

	/** Required system fields that do not yet have a file column selected. */
	get unmappedRequiredFields(): { field: string; label: string }[] {
		if (!this.mappingForm || !this.mapcols?.length) {
			return [];
		}
		return this.mapcols
			.filter((m) => {
				if (!m.req) {
					return false;
				}
				const v = this.mappingForm.get(m.field)?.value;
				return v === null || v === undefined || String(v).trim() === '';
			})
			.map((m) => ({ field: m.field, label: m.label }));
	}

	/** Plain sentence for tooltips / toasts. */
	get missingRequiredMappingSummary(): string {
		const u = this.unmappedRequiredFields;
		if (!u.length) {
			return '';
		}
		return u.map((m) => m.label).join(', ');
	}

	downloadInventoryUploadTemplate(): void {
		const defs = this.buildMapColumnDefinitions();
		const requiredFields = defs.filter((d) => d.req === true).map((d) => d.field);
		const header = requiredFields.join(',');
		const csv = `${header}\n`;
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		FileSaver.saveAs(blob, 'inventory_upload_template.csv');
	}

	remap(): void {
		this.preview = false;
	}

	investigate(row: any): void {
		const data = { data: row, update: this.optionForm.value };

		this.inventoryService.investigate(data).subscribe((r: any) => {
			this.investigation = r.result;
			this.investigated = row.stockid;
			this.modalService.open(this.eventRef, { ariaLabelledBy: 'modal-title', size: 'xl' }).result.then(
				() => {},
				() => {}
			);
		});
	}

	submit(): void {
		const data = { data: this.mappeddata, update: this.optionForm.value };
		let msg = '';
		this.inventoryService.inventoryUpload(data).subscribe((r: any) => {
			if (r.sm != null) {
				msg += 'New Items Added         :' + r.sm.new + '<br>';
				msg += 'Items Updated           :' + r.sm.updates + '<br>';
			}
			if (r.pr != null) {
				msg += 'New Prices Added        :' + r.pr.new + '<br>';
				msg += 'Prices Updated          :' + r.pr.updates + '<br>';
			}
			if (r.pd != null) {
				msg += 'New Purchase Data Added :' + r.pd.new + '<br>';
				msg += 'Purchase Data Updated   :' + r.pd.updates + '<br>';
			}
			if (r.pm != null) {
				msg += 'New Price Matrix Added  :' + r.pm.new + '<br>';
				msg += 'Price Matrix Updated    :' + r.pm.updates + '<br>';
			}
			this.mappeddata = [];
			this.uploaddata = false;
			this.files = [];
			this.resetInventoryParseState();
			this.globalSearchService.showNotification(msg, 'success', 'bottom', 'left');
		});
	}

	process(upload: any): void {
		if (this.mappingForm.invalid) {
			const detail = this.missingRequiredMappingSummary;
			const msg = detail
				? `These required fields are not mapped to a file column yet: ${detail}.`
				: 'Complete required column mappings before pre-processing.';
			this.globalSearchService.showNotification(msg, 'warning', 'bottom', 'left');
			return;
		}
		this.preview = true;
		const mapping = this.mappingForm.value;
		this.mapped = [];
		Object.entries(mapping).forEach(([key, value]) => {
			if (value !== '') {
				const col = this.mapcols.find((map) => map.field === key);
				if (col) {
					this.mapped.push(col);
				}
			}
		});
		this.mappeddata = [];
		upload.forEach((e: Record<string, unknown>) => {
			const line: Record<string, unknown> = { problem: '' };
			Object.entries(mapping).forEach(([key, value]) => {
				if (value !== '') {
					line[key] = this.cellValueForMappedColumn(e, value.toString());
				}
			});
			this.mappeddata.push(line);
		});
		const clientLines = this.mappeddata.map((line) => ({ ...line }));
		const data = { lines: clientLines };
		this.inventoryService.quickCheck(data).subscribe((results: any) => {
			const serverRows = Array.isArray(results) ? results : [];
			this.mappeddata = serverRows.map((serverRow: any, i: number) => ({
				...(clientLines[i] || {}),
				...serverRow,
			}));
			this.mappeddata.forEach((e: any) => {
				if (e.problem == null || e.problem === undefined) {
					e.problem = '';
				}
				if (!e.exists) {
					if (!e.categoryid) {
						e.problem += ' Needs Category to add Item <br>';
					}
					if (!e.lineid) {
						e.problem += ' Needs Product Line to add Item <br>';
					}
					if (!e.mfgname) {
						e.problem += ' Needs Brand to add Item <br>';
					}
				}
			});
		});
	}

	removeFile(f: any): void {
		const data = {
			batch_id: f.batch_id,
			file: f.name,
		};
		this.inventoryService.removeUpload(data).subscribe((results: any) => {
			this.filelist = results;
		});
	}

	back(): void {
		this.location.back();
	}
}
